import { Play, Square, Tag } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { formatDuration } from "@/lib/api";
import MetaPicker from "./timer-bar/MetaPicker";
import ClientMultiPicker from "./timer-bar/ClientMultiPicker";
import TimerTagPicker from "./timer-bar/TimerTagPicker";
import ManualEntryPopover from "./timer-bar/ManualEntryPopover";
import RunningStartPopover from "./timer-bar/RunningStartPopover";
import { arraysEqual } from "./timer-bar/utils";

export default function TimerBar({
  clients, activities, tags, onStart, runningEntry, onStop, onPatchRunning,
  onCreateClient, onCreateActivity, onManualCreate,
}) {
  const [description, setDescription] = useState("");
  const [clientIds, setClientIds] = useState([]);
  const [activityId, setActivityId] = useState(null);
  const [tagIds, setTagIds] = useState([]);
  const [autoActivityId, setAutoActivityId] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);
  const [startEditOpen, setStartEditOpen] = useState(false);
  const tickRef = useRef(null);
  // Track which running-entry id seeded the current local state so we can
  // distinguish a brand-new running entry (re-seed) from a server echo of
  // our own in-flight patch (don't clobber what the user is typing).
  const seededRunningIdRef = useRef(null);
  // Track the previous runningEntry id so we can detect the running→stopped
  // transition and clear the bar's input fields ready for the next entry.
  const prevRunningIdRef = useRef(null);
  const patchTimerRef = useRef(null);

  useEffect(() => {
    const prevId = prevRunningIdRef.current;
    prevRunningIdRef.current = runningEntry?.id ?? null;
    if (runningEntry) {
      if (seededRunningIdRef.current !== runningEntry.id) {
        setDescription(runningEntry.description || "");
        const seedClientIds = Array.isArray(runningEntry.client_ids) && runningEntry.client_ids.length
          ? runningEntry.client_ids
          : (runningEntry.client_id ? [runningEntry.client_id] : []);
        setClientIds(seedClientIds);
        setActivityId(runningEntry.activity_id || null);
        setTagIds(Array.isArray(runningEntry.tag_ids) ? runningEntry.tag_ids : []);
        setAutoActivityId(runningEntry.activity_id || null);
        seededRunningIdRef.current = runningEntry.id;
      }
      const startMs = new Date(runningEntry.start).getTime();
      const tick = () => setElapsed(Math.floor((Date.now() - startMs) / 1000));
      tick();
      tickRef.current = setInterval(tick, 1000);
      return () => clearInterval(tickRef.current);
    } else {
      seededRunningIdRef.current = null;
      setElapsed(0);
      clearInterval(tickRef.current);
      // Just stopped — reset the bar so the next entry starts from a blank slate
      if (prevId) {
        clearTimeout(patchTimerRef.current);
        setDescription("");
        setClientIds([]);
        setActivityId(null);
        setTagIds([]);
        setAutoActivityId(null);
      }
    }
  }, [runningEntry]);

  // Auto-apply categories when the user changes the activity in the header,
  // mirroring the EntryEditor behaviour. Already-seeded values are preserved
  // until the activity_id actually changes.
  useEffect(() => {
    if (activityId === autoActivityId) return;
    const auto = (tags || [])
      .filter((t) => Array.isArray(t.activity_ids) && t.activity_ids.includes(activityId))
      .map((t) => t.id);
    setTagIds(auto.length ? [auto[0]] : []);
    setAutoActivityId(activityId);
  }, [activityId, autoActivityId, tags]);

  // Debounced auto-patch of the running entry whenever the user edits the
  // description, client, activity, or categories in the header bar.
  useEffect(() => {
    if (!runningEntry || !onPatchRunning) return;
    const sameDesc = (runningEntry.description || "") === description;
    const runClients = Array.isArray(runningEntry.client_ids) && runningEntry.client_ids.length
      ? runningEntry.client_ids
      : (runningEntry.client_id ? [runningEntry.client_id] : []);
    const sameClients = arraysEqual(runClients, clientIds || []);
    const sameAct = (runningEntry.activity_id || null) === (activityId || null);
    const sameTags = arraysEqual(runningEntry.tag_ids || [], tagIds || []);
    if (sameDesc && sameClients && sameAct && sameTags) return;
    clearTimeout(patchTimerRef.current);
    patchTimerRef.current = setTimeout(() => {
      onPatchRunning({
        description,
        client_ids: clientIds,
        activity_id: activityId,
        tag_ids: tagIds,
      });
    }, 500);
    return () => clearTimeout(patchTimerRef.current);
  }, [description, clientIds, activityId, tagIds, runningEntry, onPatchRunning]);

  const handleStart = () => {
    if (runningEntry) {
      clearTimeout(patchTimerRef.current);
      onStop({
        ...runningEntry,
        description,
        client_ids: clientIds,
        activity_id: activityId,
        tag_ids: tagIds,
      });
    } else {
      onStart({ description, client_ids: clientIds, activity_id: activityId, tag_ids: tagIds });
    }
  };

  const handleManualSave = async (payload) => {
    const ok = await onManualCreate({ ...payload, tag_ids: tagIds });
    if (ok) {
      setDescription("");
      setClientIds([]);
      setActivityId(null);
      setTagIds([]);
      setAutoActivityId(null);
    }
  };

  return (
    <div className="sticky top-0 z-30 bg-white border-b border-slate-200 h-16 px-6 flex items-center gap-3"
         data-testid="timer-bar">
      <input
        type="text"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What are you working on?"
        className="flex-1 bg-transparent outline-none text-base placeholder:text-slate-400"
        data-testid="timer-description-input"
      />

      <ClientMultiPicker
        items={clients}
        value={clientIds}
        onChange={setClientIds}
        onCreate={onCreateClient}
        testId="timer-client-picker"
      />
      <MetaPicker
        icon={Tag}
        items={activities}
        value={activityId}
        onChange={setActivityId}
        onCreate={onCreateActivity}
        placeholder="Activity"
        defaultColor="#00b5cc"
        testId="timer-activity-picker"
      />
      <TimerTagPicker tags={tags || []} value={tagIds} onChange={setTagIds} />

      {!runningEntry && (
        <ManualEntryPopover
          open={manualOpen}
          onOpenChange={setManualOpen}
          description={description}
          clientIds={clientIds}
          activityId={activityId}
          onSave={handleManualSave}
        >
          <button
            type="button"
            className="timer-font text-xl tabular-nums w-24 text-right text-slate-700 hover:text-[#145c8c] px-1 rounded transition-colors"
            data-testid="timer-display"
            aria-label="Manual time entry"
          >
            {formatDuration(elapsed)}
          </button>
        </ManualEntryPopover>
      )}

      {runningEntry && (
        <RunningStartPopover
          open={startEditOpen}
          onOpenChange={setStartEditOpen}
          runningEntry={runningEntry}
          onSaveStart={(iso) => {
            clearTimeout(patchTimerRef.current);
            onPatchRunning && onPatchRunning({
              description,
              client_ids: clientIds,
              activity_id: activityId,
              tag_ids: tagIds,
              start: iso,
            });
          }}
        >
          <button
            type="button"
            className="timer-font text-xl tabular-nums w-24 text-right text-slate-700 hover:text-[#145c8c] px-1 rounded transition-colors"
            data-testid="timer-display-running"
            aria-label="Edit running start time"
            title="Edit start time / date"
          >
            {formatDuration(elapsed)}
          </button>
        </RunningStartPopover>
      )}

      <button
        onClick={handleStart}
        disabled={!runningEntry && clientIds.length === 0}
        className="h-11 w-11 rounded-full flex items-center justify-center text-white shadow transition-transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        style={{ background: runningEntry ? "#ef4444" : "#145c8c" }}
        data-testid={runningEntry ? "timer-stop-button" : "timer-start-button"}
        aria-label={runningEntry ? "Stop timer" : "Start timer"}
        title={!runningEntry && clientIds.length === 0 ? "Pick a client to start the timer" : ""}
      >
        {runningEntry ? (
          <Square className="h-4 w-4 fill-white" />
        ) : (
          <Play className="h-5 w-5 fill-white ml-0.5" />
        )}
      </button>
    </div>
  );
}
